import React, { lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import Login from './components/Auth/Login';
import Layout from './components/Layout/Layout';

// Pages are code-split: each one is downloaded the first time it's opened
// instead of shipping every screen (and heavy libs like recharts) up front.
// Layout wraps them in <Suspense>, so the sidebar stays put while a page loads.
//
// After a redeploy, a tab that was already open asks for the old chunk file
// names, which no longer exist. Reload once to pick up the new build rather
// than leaving a blank page.
const RELOAD_FLAG = 'chunk-reload-attempted';
const lazyPage = (factory) => lazy(() =>
    factory()
        .then((module) => {
            try { sessionStorage.removeItem(RELOAD_FLAG); } catch { /* storage unavailable */ }
            return module;
        })
        .catch((error) => {
            let alreadyTried = true;
            try {
                alreadyTried = sessionStorage.getItem(RELOAD_FLAG) === '1';
                if (!alreadyTried) sessionStorage.setItem(RELOAD_FLAG, '1');
            } catch { /* storage unavailable */ }
            if (!alreadyTried) {
                window.location.reload();
                return new Promise(() => {});
            }
            throw error;
        })
);

const EntryScreen = lazyPage(() => import('./components/Entry/EntryScreen'));
const EntryHistory = lazyPage(() => import('./components/Entry/EntryHistory'));
const Dashboard = lazyPage(() => import('./components/Admin/Dashboard'));
const FilmTypeManager = lazyPage(() => import('./components/Admin/FilmTypeManager'));
const ItemMaster = lazyPage(() => import('./components/Admin/ItemMaster'));
const CategoryManager = lazyPage(() => import('./components/Admin/CategoryManager'));
const BomManager = lazyPage(() => import('./components/Admin/BomManager'));
const StockAudit = lazyPage(() => import('./components/Admin/StockAudit'));
const Reports = lazyPage(() => import('./components/Admin/Reports'));
const RewindScreen = lazyPage(() => import('./components/Inventory/RewindScreen'));
const JumboProduction = lazyPage(() => import('./components/Inventory/JumboProduction'));
const StockOnHand = lazyPage(() => import('./components/Inventory/StockOnHand'));
const ReceiveStock = lazyPage(() => import('./components/Inventory/ReceiveStock'));
const UserManagement = lazyPage(() => import('./components/Admin/UserManagement'));
const EmployeeList = lazyPage(() => import('./components/HR/EmployeeList'));
const DailyAttendance = lazyPage(() => import('./components/HR/DailyAttendance'));
const SalaryProcessing = lazyPage(() => import('./components/HR/SalaryProcessing'));
const CompanySettings = lazyPage(() => import('./components/HR/CompanySettings'));
const ShiftWiseView = lazyPage(() => import('./components/Shifts/ShiftWiseView'));
const Guide = lazyPage(() => import('./components/Guide/Guide'));

// Loading screen
const LoadingScreen = () => (
    <div className="min-h-[100dvh] flex flex-col items-center justify-center gap-3 bg-background" role="status" aria-live="polite">
        <span className="spinner text-accent" style={{ width: '1.5rem', height: '1.5rem' }} />
        <p className="m-0 text-sm font-medium text-text-secondary">Loading your workspace…</p>
    </div>
);

// Protected route wrapper
const ProtectedRoute = ({ children }) => {
    const { isAuthenticated, loading } = useAuth();

    if (loading) {
        return <LoadingScreen />;
    }

    if (!isAuthenticated) {
        return <Navigate to="/login" replace />;
    }

    return children;
};

// Admin-only route wrapper
const AdminRoute = ({ children }) => {
    const { isAuthenticated, isAdmin, loading } = useAuth();

    if (loading) {
        return <LoadingScreen />;
    }

    if (!isAuthenticated) {
        return <Navigate to="/login" replace />;
    }

    if (!isAdmin()) {
        return <Navigate to="/" replace />;
    }

    return children;
};

function App() {
    return (
        <Routes>
            <Route path="/login" element={<Login />} />

            <Route
                path="/"
                element={
                    <ProtectedRoute>
                        <Layout>
                            <EntryScreen />
                        </Layout>
                    </ProtectedRoute>
                }
            />

            <Route
                path="/history"
                element={
                    <ProtectedRoute>
                        <Layout>
                            <EntryHistory />
                        </Layout>
                    </ProtectedRoute>
                }
            />

            <Route
                path="/shifts"
                element={
                    <ProtectedRoute>
                        <Layout>
                            <ShiftWiseView />
                        </Layout>
                    </ProtectedRoute>
                }
            />

            <Route
                path="/admin/dashboard"
                element={
                    <AdminRoute>
                        <Layout>
                            <Dashboard />
                        </Layout>
                    </AdminRoute>
                }
            />

            <Route
                path="/admin/film-types"
                element={
                    <AdminRoute>
                        <Layout>
                            <FilmTypeManager />
                        </Layout>
                    </AdminRoute>
                }
            />

            <Route
                path="/admin/users"
                element={
                    <AdminRoute>
                        <Layout>
                            <UserManagement />
                        </Layout>
                    </AdminRoute>
                }
            />

            {/* ── Modern HR module: Team · Attendance · Payroll · Settings ── */}
            <Route
                path="/hr/team"
                element={
                    <ProtectedRoute>
                        <Layout>
                            <EmployeeList />
                        </Layout>
                    </ProtectedRoute>
                }
            />

            <Route
                path="/hr/attendance"
                element={
                    <ProtectedRoute>
                        <Layout>
                            <DailyAttendance />
                        </Layout>
                    </ProtectedRoute>
                }
            />

            <Route
                path="/hr/payroll"
                element={
                    <ProtectedRoute>
                        <Layout>
                            <SalaryProcessing />
                        </Layout>
                    </ProtectedRoute>
                }
            />

            <Route
                path="/hr/settings"
                element={
                    <ProtectedRoute>
                        <Layout>
                            <CompanySettings />
                        </Layout>
                    </ProtectedRoute>
                }
            />

            {/* Old HR URLs redirect into the new pages */}
            <Route path="/hr/employees" element={<Navigate to="/hr/team" replace />} />
            <Route path="/hr/attendance/daily" element={<Navigate to="/hr/attendance" replace />} />
            <Route path="/hr/attendance/calendar" element={<Navigate to="/hr/attendance" replace />} />
            <Route path="/hr/attendance/ot" element={<Navigate to="/hr/attendance" replace />} />
            <Route path="/hr/attendance/report" element={<Navigate to="/hr/attendance" replace />} />
            <Route path="/hr/advances" element={<Navigate to="/hr/payroll?tab=advances" replace />} />
            <Route path="/hr/holidays" element={<Navigate to="/hr/attendance?tab=holidays" replace />} />
            <Route path="/hr/salary" element={<Navigate to="/hr/payroll" replace />} />
            <Route path="/hr/slips" element={<Navigate to="/hr/payroll?tab=past" replace />} />

            <Route
                path="/inventory/receive"
                element={
                    <ProtectedRoute>
                        <Layout>
                            <ReceiveStock />
                        </Layout>
                    </ProtectedRoute>
                }
            />

            <Route
                path="/admin/items"
                element={
                    <AdminRoute>
                        <Layout>
                            <ItemMaster />
                        </Layout>
                    </AdminRoute>
                }
            />

            <Route
                path="/admin/categories"
                element={
                    <AdminRoute>
                        <Layout>
                            <CategoryManager />
                        </Layout>
                    </AdminRoute>
                }
            />

            <Route
                path="/inventory/stock"
                element={
                    <ProtectedRoute>
                        <Layout>
                            <StockOnHand />
                        </Layout>
                    </ProtectedRoute>
                }
            />

            <Route
                path="/admin/bom"
                element={
                    <AdminRoute>
                        <Layout>
                            <BomManager />
                        </Layout>
                    </AdminRoute>
                }
            />

            <Route
                path="/admin/stock-audit"
                element={
                    <AdminRoute>
                        <Layout>
                            <StockAudit />
                        </Layout>
                    </AdminRoute>
                }
            />

            <Route
                path="/inventory/reports"
                element={
                    <AdminRoute>
                        <Layout>
                            <Reports />
                        </Layout>
                    </AdminRoute>
                }
            />

            <Route
                path="/inventory/rewind"
                element={
                    <ProtectedRoute>
                        <Layout>
                            <RewindScreen />
                        </Layout>
                    </ProtectedRoute>
                }
            />

            <Route
                path="/inventory/jumbo"
                element={
                    <ProtectedRoute>
                        <Layout>
                            <JumboProduction />
                        </Layout>
                    </ProtectedRoute>
                }
            />

            <Route
                path="/guide"
                element={
                    <ProtectedRoute>
                        <Layout>
                            <Guide />
                        </Layout>
                    </ProtectedRoute>
                }
            />

            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
    );
}

export default App;
